import {
	ACCOUNT_API_COMPANIES,
	SYSTEM_ENTITIES_CREATE_ENTITY,
} from 'apiRoutes';
import { FunctionComponent, useState } from 'react';
import BackButton from 'components/common/BackButton/BackButton';
import {
	Box,
	Button,
	H2,
	HStack,
	Input,
	VStack,
} from '@montel/montelpro-shared-components-v2';
import { styled } from 'styled-components';
import useSubmit, { POST } from 'hooks/useSubmit';
import useAuthRoles from 'hooks/useAuthRoles';
import { AdminRole } from 'domain/account/enums/AdminRole';
import { RequestStatus } from '@montel/montelpro-shared-components/enums';
import { useNavigate } from 'react-router-dom';
import { NewEntityModal } from './NewEntityModal';
import { NewSystemEntity } from './NewSystemEntity';
import { Company } from 'domain/company/Company';
import { CompanySelect } from 'components/companies/CompanySelect';
import { useGetOnMount } from 'hooks/useGetOnMount';
import { validateCompanyName } from 'components/companies/companyValidation';

const CreateSystemEntityPage: FunctionComponent = () => {
	const [newEntity, setNewEntity] = useState<NewSystemEntity>({
		id: '',
		name: '',
		companyId: 0,
		clientId: '',
		clientSecret: '',
	});
	const [showCredentialsModal, setShowCredentialsModal] = useState(false);

	const [errors, setErrors] = useState<{
		name?: string;
	}>({});

	const navigate = useNavigate();
	const { adminIsRestrictedToRole: isRestrictedToRole } = useAuthRoles();

	const [postSystemEntity, postStatus] = useSubmit(
		`${SYSTEM_ENTITIES_CREATE_ENTITY}`,
		POST,
		{
			messageOnError: 'An error occured, please try again!',
			messageOnSuccess: `System entity ${newEntity.name} was successfully created.`,
			showApiError: true,
		},
	);

	const { data: companies, status: companyStatus } = useGetOnMount<Company[]>(
		`${ACCOUNT_API_COMPANIES}/getcompanies`,
	);

	const onSubmit = async () => {
		const response = await postSystemEntity({
			name: newEntity.name,
			companyId: newEntity.companyId,
		});

		if (response.success) {
			setNewEntity(response.data as NewSystemEntity);
			setShowCredentialsModal(true);
		}
	};

	const onNewEntityModalClosed = async () => {
		navigate(`/systementities/${newEntity?.id}`);
	};

	return (
		<Box bg="bgMainContainer">
			<BackButton />
			<Box bg="bgMainContainer">
				<VStack gap="xl">
					<H2>Create System Entity</H2>
					<StyledInputStack gap="md" wrap="nowrap">
						<Input
							type="text"
							label="Name"
							name="name"
							value={newEntity.name}
							required
							onChange={(event) =>
								setNewEntity({
									...newEntity,
									name: event.target.value,
								})
							}
							onBlur={() =>
								setErrors({
									...errors,
									name: validateCompanyName(newEntity.name),
								})
							}
							errorMessage={errors.name}
						/>
						<CompanySelect
							companies={companies}
							value={newEntity.companyId}
							onChange={(companyId: number) =>
								setNewEntity({ ...newEntity, companyId })
							}
							loading={companyStatus === RequestStatus.LOADING}
						/>
					</StyledInputStack>
					<HStack fullWidth justify="flex-start">
						<Button
							text={'Create system entity'}
							isDisabled={
								!!validateCompanyName(newEntity.name) ||
								postStatus === RequestStatus.LOADING ||
								isRestrictedToRole(AdminRole.SALES)
							}
							onClick={onSubmit}
							type="submit"
							loadingText="Submitting"
							isLoading={postStatus === RequestStatus.LOADING}
						/>
					</HStack>
				</VStack>
				<NewEntityModal
					newEntity={newEntity}
					showModal={showCredentialsModal}
					onCloseModal={onNewEntityModalClosed}
				/>
			</Box>
		</Box>
	);
};

const StyledInputStack = styled(VStack)`
	width: 50%;
`;

export default CreateSystemEntityPage;
